import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Member-to-member messaging.
 *
 * Threads are 1:1 and are always created through the start_conversation()
 * database function, never by inserting a row from the browser — that way the
 * member pair is normalised consistently and a thread can't be forged between
 * two other people.
 */

export interface MessageRow {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

export interface ConversationRow {
  id: string;
  listing_id: string | null;
  buyer_request_id: string | null;
  subject: string | null;
  member_low: string;
  member_high: string;
  started_by: string;
  created_at: string;
  last_message_at: string;
}

export interface ConversationSummary extends ConversationRow {
  /** The person you are talking to. */
  otherMember: {
    id: string;
    full_name: string | null;
    business_name: string | null;
    county: string | null;
    cph_verified: boolean | null;
  } | null;
  lastMessage: MessageRow | null;
  unread: number;
}

const CONVERSATION_COLUMNS =
  'id, listing_id, buyer_request_id, subject, member_low, member_high, started_by, created_at, last_message_at';

/**
 * Every thread the signed-in member is part of, newest activity first, with the
 * other party, the last message and an unread count attached.
 */
export async function fetchConversations(
  supabase: SupabaseClient,
  myId: string
): Promise<ConversationSummary[]> {
  const { data: convs, error } = await supabase
    .from('conversations')
    .select(CONVERSATION_COLUMNS)
    .order('last_message_at', { ascending: false });
  if (error) throw error;

  const rows = (convs ?? []) as ConversationRow[];
  if (rows.length === 0) return [];

  const otherIds = Array.from(
    new Set(rows.map((c) => (c.member_low === myId ? c.member_high : c.member_low)))
  );

  const [{ data: profiles }, { data: messages }] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, full_name, business_name, county, cph_verified')
      .in('id', otherIds),
    supabase
      .from('messages')
      .select('id, conversation_id, sender_id, body, created_at, read_at')
      .in('conversation_id', rows.map((c) => c.id))
      .order('created_at', { ascending: true }),
  ]);

  const profileById = new Map((profiles ?? []).map((p) => [p.id, p]));
  const byConversation = new Map<string, MessageRow[]>();
  for (const m of (messages ?? []) as MessageRow[]) {
    const list = byConversation.get(m.conversation_id) ?? [];
    list.push(m);
    byConversation.set(m.conversation_id, list);
  }

  return rows.map((c) => {
    const msgs = byConversation.get(c.id) ?? [];
    const otherId = c.member_low === myId ? c.member_high : c.member_low;
    return {
      ...c,
      otherMember: profileById.get(otherId) ?? null,
      lastMessage: msgs.length ? msgs[msgs.length - 1] : null,
      unread: msgs.filter((m) => m.sender_id !== myId && !m.read_at).length,
    };
  });
}

export async function fetchMessages(
  supabase: SupabaseClient,
  conversationId: string
): Promise<MessageRow[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('id, conversation_id, sender_id, body, created_at, read_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as MessageRow[];
}

export async function sendMessage(
  supabase: SupabaseClient,
  conversationId: string,
  body: string
): Promise<MessageRow> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('You need to be signed in to send a message.');

  const { data, error } = await supabase
    .from('messages')
    .insert({ conversation_id: conversationId, sender_id: user.id, body: body.trim() })
    .select('id, conversation_id, sender_id, body, created_at, read_at')
    .single();
  if (error) throw error;
  return data as MessageRow;
}

/** Marks the other party's messages in a thread as read. */
export async function markRead(
  supabase: SupabaseClient,
  conversationId: string,
  myId: string
): Promise<void> {
  await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .neq('sender_id', myId)
    .is('read_at', null);
}

export interface StartConversationOptions {
  otherMemberId: string;
  listingId?: string | null;
  buyerRequestId?: string | null;
  subject?: string | null;
}

/** Returns the thread id, reusing an existing thread where there is one. */
export async function startConversation(
  supabase: SupabaseClient,
  opts: StartConversationOptions
): Promise<string> {
  const { data, error } = await supabase.rpc('start_conversation', {
    other_member: opts.otherMemberId,
    listing: opts.listingId ?? null,
    buyer_request: opts.buyerRequestId ?? null,
    subject_text: opts.subject ?? null,
  });
  if (error) throw error;
  return data as string;
}

/** Start a thread and post the opening message in one go. */
export async function startConversationWithMessage(
  supabase: SupabaseClient,
  opts: StartConversationOptions & { body: string }
): Promise<string> {
  const conversationId = await startConversation(supabase, opts);
  await sendMessage(supabase, conversationId, opts.body);
  return conversationId;
}

export async function fetchUnreadCount(supabase: SupabaseClient): Promise<number> {
  const { data, error } = await supabase.rpc('unread_message_count');
  if (error) return 0;
  return Number(data) || 0;
}

export function memberLabel(
  member: ConversationSummary['otherMember']
): string {
  if (!member) return 'FarmTradeDirect member';
  return member.business_name || member.full_name || 'FarmTradeDirect member';
}

export function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

/** "10:42", "Yesterday", "3 Aug" — compact, like a phone's message list. */
export function messageTime(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const sameDay = then.toDateString() === now.toDateString();
  if (sameDay) {
    return then.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (then.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Full timestamp for inside a thread. */
export function messageStamp(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  return then.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
